import React, { useState, useEffect } from 'react';
import {
  Button, CircularProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { endpoints } from '../../services/apiClient';
import { User } from '../../types/schema';
import AddUserModal from '../common/AddUserModal';
import DeleteUserModal from '../common/DeleteUserModal';
import ResetPasswordModal from '../common/ResetPasswordModal';
import { useAccountContext } from '../../hooks/useAccountContext';

const UserManagementView: React.FC = () => {
  const selectedAccount = useSelector((state: RootState) => state.app.selectedAccount);
  const { accountId } = useAccountContext();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (selectedAccount) {
      const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
          const response = await endpoints.adminGetUsersForAccount(selectedAccount.accountId.toString(), accountId);
          setUsers(response.users);
        } catch (error) {
          console.error(`Failed to fetch users for account ${selectedAccount.accountId}`, error);
          setUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      };
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [selectedAccount, accountId]);


  const handleOpenAddUserModal = () => setAddUserModalOpen(true);
  const handleCloseAddUserModal = () => { setAddUserModalOpen(false); setAddUserError(null); };

  const handleOpenDeleteModal = (user: User) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setSelectedUser(null);
    setDeleteModalOpen(false);
  };

  const handleOpenResetPasswordModal = (user: User) => {
    setSelectedUser(user);
    setResetPasswordModalOpen(true);
  };
  const handleCloseResetPasswordModal = () => {
    setSelectedUser(null);
    setResetPasswordModalOpen(false);
  };

  const handleAddUser = async (userData: { email: string; given_name: string; family_name: string }) => {
    if (!selectedAccount) return;
    try {
      const full_name = `${userData.given_name} ${userData.family_name}`.trim();
      await endpoints.adminAddUserToAccount(selectedAccount.accountId.toString(), { ...userData, full_name }, accountId);
      // Refresh users list
      const response = await endpoints.adminGetUsersForAccount(selectedAccount.accountId.toString(), accountId);
      setUsers(response.users);
      handleCloseAddUserModal();
    } catch (error) {
      console.error('Failed to add user', error);
      setAddUserError(error instanceof Error ? error.message : 'Failed to add user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await endpoints.adminDeleteUser(selectedUser.email, accountId);
      // Refresh users list
      if (selectedAccount) {
        const response = await endpoints.adminGetUsersForAccount(selectedAccount.accountId.toString(), accountId);
        setUsers(response.users);
      }
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Failed to delete user', error);
    }
  };

  const handleResetPassword = async (password: string) => {
    if (!selectedUser) return;
    try {
      await endpoints.adminResetPassword(selectedUser.email, { password }, accountId);
      handleCloseResetPasswordModal();
    } catch (error) {
      console.error('Failed to reset password', error);
    }
  };


  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {selectedAccount ? (
        <>
          <Typography variant="h6" component="h2" gutterBottom>
            Managing Users for: {selectedAccount.name}
          </Typography>
          <Button variant="contained" sx={{ mb: 2 }} onClick={handleOpenAddUserModal}>
            Add New User
          </Button>
          {isLoadingUsers ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <IconButton aria-label="reset password" onClick={() => handleOpenResetPasswordModal(user)}>
                          <VpnKeyIcon />
                        </IconButton>
                        <IconButton aria-label="delete" onClick={() => handleOpenDeleteModal(user)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Typography>
          Please select an account from the account switcher to manage users.
        </Typography>
      )}

      <AddUserModal open={isAddUserModalOpen} onClose={handleCloseAddUserModal} onAddUser={handleAddUser} errorMessage={addUserError} />
      <DeleteUserModal open={isDeleteModalOpen} onClose={handleCloseDeleteModal} onConfirmDelete={handleDeleteUser} user={selectedUser} />
      <ResetPasswordModal open={isResetPasswordModalOpen} onClose={handleCloseResetPasswordModal} onConfirmReset={handleResetPassword} user={selectedUser} />
    </Paper>
  );
};

export default UserManagementView;
